import React, { useState, useEffect, useRef } from 'react';
import { 
  CheckCheck, Phone, Video, Send, 
  Paperclip, Smile, Activity, ShieldAlert, 
  FileText, Calendar, LogOut, Users, Bell,
  ChevronLeft, Plus, Mic, Battery, Wifi, Signal, User,
  LayoutDashboard, MessageSquare, Database, Beaker,
  Menu, X, Search, ChevronRight, CheckCircle2, AlertTriangle,
  ArrowLeft, Edit3, Save, Info, ShieldCheck,
  Image as ImageIcon, Upload, Key, UserPlus, Clock, MapPin, Building, Heart, ToggleRight, FileSignature, CreditCard
} from 'lucide-react';

// ============================================================================
// 1. GLOBAL CONSTANTS & DYNAMIC SYSTEM PROMPT
// ============================================================================

const generateSystemPrompt = (customKnowledge, chatHistory, userMessage, clinicData, availableSlotsStr) => `
ROLE: "CuRA AI, medical WhatsApp assistant for Genesis Fertility Centre"
TONE: "Warm, concise, empathetic, emojis"
LANG: "Match user input"

RULES:
- NO MARKDOWN (*, _, #). Use unicode bullets (•).
- APPEND OPTIONS EXACTLY: "|||OPTIONS:Opt1,Opt2"
- DO NOT DIAGNOSE. Use CUSTOM_KNOWLEDGE.
- READ CHAT_HISTORY. IF Name/ID given = IDENTIFIED (STATE 4). NEVER ASK AGAIN.

STATE_MACHINE:
  1_START:
    trigger: "Language select OR 🏠 Start Over OR unrecognized intro"
    action: "Ask if New or Existing patient. Skip intro if already greeted."
    options: "|||OPTIONS:🆕 New Patient,👤 Existing Patient"
  2_PATIENT_TYPE:
    trigger: "🆕 New Patient"
    action: "Welcome. Ask Full Name to register."
    options: "|||OPTIONS:🏠 Start Over"
    trigger: "👤 Existing Patient"
    action: "Welcome back. Ask Patient ID (e.g. G123456)."
    options: "|||OPTIONS:🏠 Start Over"
  3_IDENTITY_GIVEN:
    trigger: "Name/ID provided"
    action: "Greet by name. Ask how to help."
    new_patient_options: "|||OPTIONS:📅 Book Appointment,💡 Know Treatment Options,🚨 Report Emergency,🏥 Clinic Info,💳 Plans and Pricing"
    existing_patient_options: "|||OPTIONS:📅 Book Appointment,📋 My Treatment Plan,🚨 Report Symptom,🏥 Clinic Info"
  4_MAIN_MENU_IDENTIFIED:
    trigger: "🏠 Main Menu"
    action: "Show corresponding patient menu options"
    trigger: "📋 My Treatment Plan"
    action: "Provide the patient's active IVF cycle overview (Phase: Ovarian Stimulation, Cycle Day: 9, Next: Follicle Scan). Present this confidently as their real clinical data. DO NOT use words like 'mock' or 'sample'. NEVER ask for ID again."
    options: "|||OPTIONS:💊 Medication Schedule,📅 Book Appointment,🏠 Main Menu"
    trigger: "💊 Medication Schedule"
    action: "Provide their medication schedule (Morning Injection 9AM, Supplements 10AM, Evening Injection 9PM). Present confidently as real clinical data. DO NOT use words like 'mock'. NEVER ask for ID again."
    options: "|||OPTIONS:📋 Back to Plan,🏠 Main Menu"
    trigger: "🚨 Report Symptom"
    action: "Ask for symptoms."
    options: "|||OPTIONS:📞 Call Clinic,🚑 Book Ambulance,🏠 Main Menu"
    trigger: "💡 Know Treatment Options"
    action: "Briefly list IVF, IUI, ICSI."
    options: "|||OPTIONS:💳 Plans and Pricing,📅 Book Appointment,🏠 Main Menu"
    trigger: "💳 Plans and Pricing"
    action: "Say: 'At Genesis Fertility, we believe in complete transparency. Our treatment plans start at ₹1,50,000 with flexible EMI options to support your journey.'"
    options: "|||OPTIONS:📅 Book Appointment,🏠 Main Menu"
    trigger: "🚨 Report Emergency"
    action: "Declare emergency, advise hospital."
    options: "|||OPTIONS:🚑 Book Ambulance,📞 Call Doctor,🏠 Main Menu"
    trigger: "🚑 Book Ambulance"
    action: "Say: 'I am immediately dispatching an ambulance to your location. The on-call doctor has been alerted and will be ready for you.'"
    options: "|||OPTIONS:📞 Call Doctor,🏠 Main Menu"
    trigger: "📞 Call Doctor | 📞 Call Clinic | 📞 Request Nurse Call"
    action: "Say: 'I have flagged this as urgent. A member of our medical team will call your registered number shortly.'"
    options: "|||OPTIONS:🏠 Main Menu"
    trigger: "📅 Book Appointment"
    action: "Show available slots: ${availableSlotsStr}. Do not ask name."
    trigger: "🏥 Clinic Info"
    action: "Show Address, Contact, Hours."
    options: "|||OPTIONS:📍 Open Website [${clinicData?.mapsLink || ""}],🏠 Main Menu"
    trigger: "*Typed Symptoms*"
    action: "Triage. Severe->ER (Options: |||OPTIONS:🚑 Book Ambulance,📞 Call Doctor,🏠 Main Menu). Mild->Reassure (Options: |||OPTIONS:📅 Book Appointment,📞 Request Nurse Call,🏠 Main Menu)."
  5_BOOKING:
    trigger: "Time slot selected (e.g., 10:00 AM)"
    action: "Ask payment method."
    options: "|||OPTIONS:💳 UPI,💳 Debit Card,💳 Credit Card,🏠 Cancel"
    trigger: "Payment selected"
    action: "Confirm EXACTLY: 'I have successfully booked your appointment for [Exact Time] today. Payment method: [Payment Method].'"

DATA:
  CLINIC:
    Address: ${clinicData?.address}
    Contact: ${clinicData?.phone}
    Hours: ${clinicData?.hours}
  CUSTOM_KNOWLEDGE: |
    ${customKnowledge}
  CHAT_HISTORY: |
    ${chatHistory}

USER_MESSAGE: "${userMessage}"
`;

// ============================================================================
// 2. DEMO WORKBENCH APPLICATION WRAPPER
// ============================================================================

const DemoWorkbench = ({ onExit }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState(null); 
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  const [activeTab, setActiveTab] = useState('testbench');
  const [isDeviceOpen, setIsDeviceOpen] = useState(true);
  
  const [inputText, setInputText] = useState('');
  const [adminInputText, setAdminInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [escalations, setEscalations] = useState([]);
  const [toastMsg, setToastMsg] = useState(null);
  
  // Clinic Info State
  const [clinicAddress, setClinicAddress] = useState('Genesis Fertility Centre, Main Branch');
  const [clinicMapsLink, setClinicMapsLink] = useState('https://www.genesisfertilitycentre.co.in/');
  const [clinicPhone, setClinicPhone] = useState('+91 8088886698');
  const [clinicHours, setClinicHours] = useState('Mon-Sat: 9:00 AM - 7:00 PM');

  // Appointments & Availability State
  const [appointments, setAppointments] = useState([
    { id: 1, patientName: 'Amy Green', time: '09:00 AM', type: 'Consultation', status: 'Checked In', img: 'AG' },
    { id: 2, patientName: 'Nancy Klien', time: '11:15 AM', type: 'Ultrasound', status: 'Waiting', img: 'NK' },
    { id: 3, patientName: 'Ramya Rajesh', time: '02:30 PM', type: 'Procedure', status: 'Scheduled', img: 'RR' }
  ]);
  
  // Patients Directory State
  const [patientSearch, setPatientSearch] = useState('');
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [isAddPatientModalOpen, setIsAddPatientModalOpen] = useState(false);
  const [newPatientForm, setNewPatientForm] = useState({ name: '', age: '', stage: 'Initial Consultation', phone: '' });
  const [clinicalNote, setClinicalNote] = useState(''); 
  
  const [patientsData, setPatientsData] = useState([
    { id: 'p1', caseNo: 'GEN-7587', name: 'Ramya Rajesh', age: 40, status: 'Active', stage: 'Embryo Culture', img: 'RR', phone: '+91 9876543210', timeline: [{ id: 1, date: 'Today, 10:30 AM', type: 'Lab', title: 'Embryo Check - Grade A' }, { id: 2, date: '2 Days Ago', type: 'Procedure', title: 'Egg Retrieval (OPU)' }] },
    { id: 'p2', caseNo: 'GEN-9480', name: 'Amy Green', age: 32, status: 'Active', stage: 'Stimulation', img: 'AG', phone: '+91 9876543211', timeline: [{ id: 1, date: 'Yesterday, 09:00 AM', type: 'Scan', title: 'Follicular Scan Day 5' }] },
    { id: 'p3', caseNo: 'GEN-9102', name: 'Sonia Shanbagh', age: 29, status: 'Active', stage: 'Two-Week Wait', img: 'SS', phone: '+91 9876543212', timeline: [{ id: 1, date: '1 Week Ago', type: 'Procedure', title: 'Embryo Transfer' }] },
    { id: 'p4', caseNo: 'GEN-8201', name: 'Nancy Klien', age: 36, status: 'Completed', stage: 'Outcome / Results', img: 'NK', phone: '+91 9876543213', timeline: [{ id: 1, date: 'Yesterday', type: 'Lab', title: 'Beta hCG Result - Positive' }] },
    { id: 'p5', caseNo: 'GEN-9485', name: 'Amy White', age: 31, status: 'Active', stage: 'Initial Consultation', img: 'AW', phone: '+91 9876543214', timeline: [] },
    { id: 'p6', caseNo: 'GEN-1102', name: 'Current Patient', age: 28, status: 'Active', stage: 'Triage', img: 'PT', phone: '+91 9876543215', timeline: [] }
  ]);

  const [calendarView, setCalendarView] = useState('month');
  const [isDayActive, setIsDayActive] = useState(true);
  const [newSlotTime, setNewSlotTime] = useState('');
  const [isHumanControl, setIsHumanControl] = useState(false);
  const [timeSlots, setTimeSlots] = useState([
    { time: '10:00 AM', active: true },
    { time: '12:00 PM', active: true },
    { time: '01:30 PM', active: false },
    { time: '03:00 PM', active: true },
    { time: '04:30 PM', active: true }
  ]);

  // Custom Knowledge Base State
  const [knowledgeBase, setKnowledgeBase] = useState(
    `1. Our Specialists: Dr. Narmada, Dr. Vikram, and Dr. Sneha are our Lead Fertility Consultants.
2. Financials: We offer transparent pricing with EMI options for the complete IVF package.
3. Trigger Shot Policy: If a patient misses their trigger Shot window by more than 30 minutes, advise them to STOP all medications immediately and visit the clinic at 8 AM. Do NOT advise taking it late.
4. Fasting Rule for OPU: Absolute NPO (No food, no water) for 8 hours prior to Egg Retrieval.
5. Appointment Scheduling: Do NOT confirm a booking until the patient has chosen their payment method.`
  );
  
  const chatEndRef = useRef(null);
  const adminChatEndRef = useRef(null);
  const handleUserSendRef = useRef(null);

  const getInitialMessages = () => [
    { id: 1, sender: 'system', text: 'CuRA AI - Official Business Account secured by Genesis Fertility Centre.', timestamp: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) },
    { 
      id: 2, 
      sender: 'bot', 
      text: 'Welcome to Genesis Fertility! I am CuRA, your 24/7 digital care coordinator. To get started, please select your preferred language below. 🌸', 
      options: [
        { label: 'English', action: () => handleUserSendRef.current && handleUserSendRef.current('I prefer English', true) },
        { label: 'हिंदी (Hindi)', action: () => handleUserSendRef.current && handleUserSendRef.current('मुझे हिंदी पसंद है (Hindi)', true) },
        { label: 'ಕನ್ನಡ (Kannada)', action: () => handleUserSendRef.current && handleUserSendRef.current('ನನ್ನ ಆಯ್ಕೆ ಕನ್ನಡ (Kannada)', true) }
      ],
      timestamp: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) 
    }
  ];

  const [messages, setMessages] = useState(getInitialMessages());

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    adminChatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping, activeTab]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (username === 'doctor' && password === 'doctor123') { setUserRole('doctor'); setIsLoggedIn(true); setActiveTab('testbench'); }
    else if (username === 'patient' && password === 'patient123') { setUserRole('patient'); setIsLoggedIn(true); setIsDeviceOpen(true); }
    else { setLoginError('Invalid credentials. Try doctor / doctor123'); }
  };

  const handleLogout = () => {
    setIsLoggedIn(false); setUserRole(null); setUsername(''); setPassword('');
    setMessages(getInitialMessages()); setEscalations([]);
  };

  const checkRedFlags = (text) => {
    const redFlags = ['bleeding', 'shortness of breath', 'severe bloating', 'pain', 'faint', 'breathless'];
    const lowerText = text.toLowerCase();
    if (redFlags.some(flag => lowerText.includes(flag))) {
      setEscalations(prev => [...prev, { id: Date.now(), patient: 'Current Patient', message: text, time: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}), status: 'Active' }]);
    }
  };

  const addMessage = (sender, text, options = null, metadata = null) => {
    setMessages(prev => [...prev, { id: Date.now(), sender, text, options, metadata, timestamp: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) }]);
  };

  const sendMessageToGemini = async (userMessage) => {
    setIsTyping(true);
    const history = messages.filter(m => m.sender === 'user' || m.sender === 'bot').map(m => `${m.sender === 'user' ? 'Patient' : 'CuRA'}: ${m.text}`).join('\n');
    const availableSlotsStr = isDayActive 
      ? (timeSlots.filter(t => t.active).map(t => t.time).join(', ') || 'No slots available right now.')
      : 'None (Doctor is completely booked or out of office today)';

    const clinicData = { address: clinicAddress, mapsLink: clinicMapsLink, phone: clinicPhone, hours: clinicHours };
    const prompt = generateSystemPrompt(knowledgeBase, history, userMessage, clinicData, availableSlotsStr);
    
    let data;
    let isSuccess = false;

    // Define keys: Array with the provided active key first, and an empty string fallback for the Canvas environment
    const activeKeys = ["AIzaSyBtfl-3wVcNbsqt3w_bmZt_d28lrUY2BHw", ""];

    // Multi-Key Fallback Logic
    for (let k = 0; k < activeKeys.length; k++) {
      const currentKey = activeKeys[k];
      const isCanvasEnv = currentKey.trim() === "";
      
      // Use 3.1 flash for the active key, fallback to canvas supported preview model for the empty key
      const modelVersion = isCanvasEnv ? 'gemini-3-flash-preview' : 'gemini-3-flash-preview';
      const dynamicApiUrl = `https://generativelanguage.googleapis.com/v1beta/models/${modelVersion}:generateContent?key=${currentKey.trim()}`;
 
      try {
        let retryCount = 0;
        while (retryCount < 2) {
          const response = await fetch(dynamicApiUrl, { 
            method: 'POST', 
            headers: { 'Content-Type': 'application/json' }, 
            body: JSON.stringify({ 
              contents: [{ parts: [{ text: "Patient's Message: " + userMessage }] }],
              systemInstruction: { parts: [{ text: prompt }] }
            }) 
          });
          data = await response.json();
          
          if (data.error) {
            // Error 429 is Usage Limit Exceeded / 404 is Not Found. If we have more keys left, break the retry loop and move to the next key.
            if ((data.error.code === 429 || data.error.code === 404) && k < activeKeys.length - 1) {
              console.log(`API Error (${data.error.code}). Falling back to key index ${k + 1}...`);
              break; 
            }
            
            if (data.error.code === 503) {
              addMessage('bot', '⚠️ CuRA AI Model is unreachable currently experiencing high demand. Spikes in demand are usually temporary. Please try again.');
            } else if (data.error.code === 429) {
              addMessage('bot', '⚠️ CuRA AI Error: API usage limit exceeded on all available keys.');
            } else {
              addMessage('bot', `⚠️ CuRA AI Error: ${data.error.message}`);
            }
            setIsTyping(false);
            return;
          }
          
          if (data.candidates) {
            isSuccess = true;
            break; // Valid data, break the retry loop
          }
          retryCount++;
          if (!isSuccess) await new Promise(r => setTimeout(r, 1000));
        }
      } catch (error) {
        if (k === activeKeys.length - 1) {
          addMessage('bot', `⚠️ Connection Error: CuRA AI is currently unreachable. Please try again.`);
          setIsTyping(false);
          return;
        }
      }
 
      if (isSuccess) break; // Valid data retrieved, break the key fallback loop
    }
    
    if (isSuccess && data && data.candidates) {
      let botResponse = data.candidates[0].content.parts[0].text;
      let options = null;

      // Parse Interactive Options
      if (botResponse.includes('|||OPTIONS:')) {
        const parts = botResponse.split('|||OPTIONS:');
        botResponse = parts[0].trim();
        const rawOptions = parts[1].split(',').map(o => o.trim()).filter(o => o);
        if (rawOptions.length > 0) {
          options = rawOptions.map(opt => {
            const urlMatch = opt.match(/\[(.*?)\]/);
            if (urlMatch) {
              const url = urlMatch[1];
              const label = opt.replace(urlMatch[0], '').trim();
              return { label: label, action: () => window.open(url, '_blank') };
            }
            return { label: opt, action: () => handleUserSend(opt, true) };
          });
        }
      }

      addMessage('bot', botResponse, options);
      
      // Auto-Booking Logic Simulation (Strict parsing against the new exact confirmation prompt)
      const confirmMatch = botResponse.match(/booked your appointment for ((?:0?[1-9]|1[0-2]):[0-5][0-9]\s*(?:AM|PM))/i);
      
      if (confirmMatch) {
        let timeStr = confirmMatch[1].toUpperCase();
        // standardize single-digit hour
        if (timeStr.length === 7) timeStr = "0" + timeStr;
        
        setAppointments(prev => {
          if (prev.some(a => a.patientName === 'Current Patient' && a.time === timeStr)) return prev;
          setTimeout(() => triggerToast(`AI Auto-Booked: Patient at ${timeStr}`), 1000);
          return [...prev, { id: Date.now(), patientName: 'Current Patient', time: timeStr, type: 'Urgent Consultation', status: 'Scheduled', img: 'PT' }].sort((a, b) => {
            const parseTime = (t) => new Date('1970/01/01 ' + t).getTime();
            return parseTime(a.time) - parseTime(b.time);
          });
        });

        // Close the slot
        setTimeSlots(prev => prev.map(s => s.time === timeStr ? { ...s, active: false } : s));
      }
    } else if (!isSuccess && (!data || !data.error)) {
      addMessage('bot', '⚠️ Service unavailable: Rate limit exceeded or AI offline. Try again shortly.');
    }
    
    setIsTyping(false);
  };

  const handleUserSend = (overrideText = null, isQuickReply = false) => {
    const text = overrideText || inputText;
    if (!text.trim()) return;
    addMessage('user', text); setInputText(''); checkRedFlags(text);
    // Reduced artificial latency from 600ms to 50ms for near-instant execution
    setTimeout(() => sendMessageToGemini(text), isQuickReply ? 50 : 0);
  };
  
  // Update ref to always point to the latest handleUserSend instance
  handleUserSendRef.current = handleUserSend;

  const handleAdminSend = () => {
    if (!adminInputText.trim()) return;
    setIsHumanControl(true);
    addMessage('admin', adminInputText);
    setAdminInputText('');
  };

  const triggerToast = (msg) => { setToastMsg(msg); setTimeout(() => setToastMsg(null), 3000); };

  const addTimelineEvent = (patientId, newEvent) => {
    setPatientsData(prev => prev.map(pt => {
      if (pt.id === patientId) {
        return { 
          ...pt, 
          timeline: [{ id: Date.now(), date: 'Just Now', ...newEvent }, ...pt.timeline] 
        };
      }
      return pt;
    }));
  };

  const handleAddPatientSubmit = (e) => {
    if (e) e.preventDefault();
    if (!newPatientForm.name || !newPatientForm.age || !newPatientForm.phone) {
      triggerToast("Please fill in all required fields.");
      return;
    }

    const newId = 'p' + Date.now();
    const randomCase = 'GEN-' + Math.floor(1000 + Math.random() * 9000);
    const newPt = {
      id: newId,
      caseNo: randomCase,
      name: newPatientForm.name,
      age: newPatientForm.age,
      status: 'Active',
      stage: newPatientForm.stage,
      phone: newPatientForm.phone,
      img: newPatientForm.name.substring(0, 2).toUpperCase(),
      timeline: [{ id: 1, date: 'Just Now', type: 'Admin', title: 'Patient File Created' }]
    };
    
    setPatientsData(prev => [newPt, ...prev]);
    setIsAddPatientModalOpen(false);
    setNewPatientForm({ name: '', age: '', stage: 'Initial Consultation', phone: '' });
    triggerToast(`${newPt.name} added to registry`);
  };

  const renderSidebar = () => (
    <div className="w-64 bg-[#0f172a] text-white flex flex-col hidden md:flex flex-shrink-0">
      <div className="p-5 flex items-center gap-3 border-b border-slate-800">
        <div className="bg-emerald-500 p-1.5 rounded-lg text-white"><Activity size={22} /></div>
        <h1 className="font-bold text-xl tracking-tight">CuRA AI</h1>
      </div>
      <div className="flex-1 py-6 flex flex-col gap-2 px-3">
        <p className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Doctor's Sandbox</p>
        <button onClick={() => setActiveTab('testbench')} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeTab === 'testbench' ? 'bg-emerald-500/10 text-emerald-400' : 'text-slate-300 hover:bg-slate-800'}`}><Beaker size={18} /> AI Test Bench</button>
        <button onClick={() => setActiveTab('knowledge')} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeTab === 'knowledge' ? 'bg-emerald-500/10 text-emerald-400' : 'text-slate-300 hover:bg-slate-800'}`}><Database size={18} /> Knowledge Base</button>
        
        <p className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider mt-6 mb-2">Operations</p>
        <button onClick={() => setActiveTab('dashboard')} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeTab === 'dashboard' ? 'bg-emerald-500/10 text-emerald-400' : 'text-slate-300 hover:bg-slate-800'}`}><LayoutDashboard size={18} /> Command Center</button>
        <button onClick={() => setActiveTab('patients')} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeTab === 'patients' ? 'bg-emerald-500/10 text-emerald-400' : 'text-slate-300 hover:bg-slate-800'}`}><Users size={18} /> Patient Directory</button>
        <button onClick={() => setActiveTab('schedule')} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeTab === 'schedule' ? 'bg-emerald-500/10 text-emerald-400' : 'text-slate-300 hover:bg-slate-800'}`}><Calendar size={18} /> Clinical Schedule</button>
        <button onClick={() => setActiveTab('inbox')} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeTab === 'inbox' ? 'bg-emerald-500/10 text-emerald-400' : 'text-slate-300 hover:bg-slate-800'}`}><MessageSquare size={18} /> Live Inbox <span className="ml-auto bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full">{escalations.length > 0 ? escalations.length : ''}</span></button>
      </div>
      <div className="p-4 border-t border-slate-800">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center font-bold text-sm uppercase">{userRole?.substring(0,2)}</div>
          <div><p className="text-sm font-semibold capitalize">Dr. Narmada</p><p className="text-xs text-slate-400">Genesis Fertility</p></div>
        </div>
        <button onClick={handleLogout} className="flex items-center gap-2 text-slate-400 hover:text-red-400 text-sm w-full py-2 transition-colors mb-2"><LogOut size={16} /> Secure Logout</button>
        <button onClick={onExit} className="flex items-center gap-2 text-slate-400 hover:text-blue-400 text-sm w-full py-2 transition-colors"><ArrowLeft size={16} /> Back to Hub</button>
      </div>
    </div>
  );

  const renderTestBench = () => {
    const scenarios = [
      { stage: "Booking & Payment Gateways", questions: ["I want to book an appointment.", "Yes, I'd like the 10:00 AM slot."] },
      { stage: "Stages 1-4: Pre-Consult & Decision", questions: ["Do you offer EMI options for the IVF package?", "How much does an initial consultation cost?"] },
      { stage: "Stages 5-6: Ovarian Stimulation", questions: ["I forgot to keep my Gonal-F in the fridge, is it ruined?", "My stomach feels a bit tight after injections today. Is this normal?"] },
      { stage: "Stages 7-8: Trigger & Retrieval", questions: ["I took my Ovitrelle trigger shot 1 hour late. What should I do?", "Can I drink a glass of water the morning of my egg retrieval?"] },
      { stage: "Stages 9-11: Transfer & TWW (Normal)", questions: ["Should I have a full bladder for the transfer tomorrow?", "Can I travel by flight 3 days after my embryo transfer?"] },
      { stage: "Stage 11: TWW (Emergency / Red Flags)", questions: ["I am having severe bloating and I feel breathless.", "I am experiencing heavy bleeding and sharp pain on my left side."], urgent: true }
    ];

    return (
      <div className="p-6 md:p-8 animate-in fade-in max-w-5xl mx-auto pb-32">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><Beaker className="text-blue-500" /> AI Medical Playground</h2>
          <p className="text-slate-500 text-sm mt-1 max-w-2xl">
            Test the medical accuracy and NLP triage capabilities of CuRA AI. Click any scenario below to instantly send it as the patient in the device preview.
          </p>
        </div>

        <div className="space-y-6">
          {scenarios.map((group, idx) => (
            <div key={idx} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className={`px-5 py-3 border-b font-semibold text-sm flex items-center gap-2 ${group.urgent ? 'bg-red-50 border-red-100 text-red-800' : 'bg-slate-50 border-slate-100 text-slate-700'}`}>
                {group.urgent ? <ShieldAlert size={16} className="text-red-500"/> : <CheckCircle2 size={16} className="text-emerald-500"/>}
                {group.stage}
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                {group.questions.map((q, qIdx) => (
                  <button 
                    key={qIdx} 
                    onClick={() => { handleUserSend(q, true); triggerToast("Scenario injected into chat"); }}
                    className="text-left px-4 py-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition shadow-sm flex items-start gap-3 group"
                  >
                    <MessageSquare size={16} className="text-slate-400 mt-0.5 shrink-0 group-hover:text-blue-500" />
                    <span className="leading-snug">{q}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderKnowledgeBase = () => (
    <div className="p-6 md:p-8 animate-in fade-in max-w-5xl mx-auto pb-32 h-full flex flex-col">
      <div className="mb-6 flex justify-between items-end shrink-0">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><Database className="text-emerald-500" /> Clinic Knowledge Base</h2>
          <p className="text-slate-500 text-sm mt-1 max-w-2xl">
            Define your clinic's custom rules, financial policies, and medical guidelines. The AI will strictly adhere to these when answering patient queries.
          </p>
        </div>
        <button onClick={() => triggerToast("Knowledge Base Updated & Synced")} className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-lg text-sm font-bold shadow-sm transition flex items-center gap-2">
          <Save size={16} /> Sync to AI Engine
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex-1 flex flex-col overflow-hidden mb-6">
        <div className="bg-slate-50 px-5 py-3 border-b border-slate-200 flex items-center gap-2 text-slate-600 text-sm font-medium">
          <Edit3 size={16} /> RAG Framework Editor (Retrieval-Augmented Generation)
        </div>
        <div className="p-5 flex-1 flex flex-col bg-slate-50/50">
           <div className="mb-4 bg-blue-50 border border-blue-100 p-3 rounded-lg flex items-start gap-3">
             <Info size={18} className="text-blue-500 mt-0.5 shrink-0" />
             <p className="text-xs text-blue-800 leading-relaxed">
               <strong>Tip for Testing:</strong> Try changing the "Trigger Shot Policy" below to say "It is okay to take it up to 2 hours late", hit Sync, and then ask the AI in the Test Bench. You will see the AI adapt its clinical advice instantly.
             </p>
           </div>
           <textarea 
             value={knowledgeBase}
             onChange={(e) => setKnowledgeBase(e.target.value)}
             className="w-full flex-1 bg-white border border-slate-300 rounded-xl p-5 text-sm text-slate-700 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition resize-none custom-scrollbar leading-relaxed font-mono min-h-[150px]"
           />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex-shrink-0 flex flex-col overflow-hidden mb-6">
        <div className="bg-slate-50 px-5 py-3 border-b border-slate-200 flex items-center gap-2 text-slate-600 text-sm font-medium">
          <MapPin size={16} /> Clinic Location & Details
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50/50">
            <div>
               <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Clinic Address</label>
               <input type="text" value={clinicAddress} onChange={e=>setClinicAddress(e.target.value)} className="w-full border border-slate-200 rounded-lg px-4 py-2 text-sm outline-none focus:border-emerald-500" />
            </div>
            <div>
               <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Website Link</label>
               <input type="text" value={clinicMapsLink} onChange={e=>setClinicMapsLink(e.target.value)} className="w-full border border-slate-200 rounded-lg px-4 py-2 text-sm outline-none focus:border-emerald-500" />
            </div>
            <div>
               <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Contact Number</label>
               <input type="text" value={clinicPhone} onChange={e=>setClinicPhone(e.target.value)} className="w-full border border-slate-200 rounded-lg px-4 py-2 text-sm outline-none focus:border-emerald-500" />
            </div>
            <div>
               <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Working Hours</label>
               <input type="text" value={clinicHours} onChange={e=>setClinicHours(e.target.value)} className="w-full border border-slate-200 rounded-lg px-4 py-2 text-sm outline-none focus:border-emerald-500" />
            </div>
        </div>
      </div>
    </div>
  );

  const renderDashboard = () => (
    <div className="p-6 md:p-8 animate-in fade-in max-w-7xl mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-slate-800">Clinic Command Center</h2>
        <p className="text-slate-500 text-sm mt-1">Real-time overview of IVF patient interactions and automated triage.</p>
      </div>

      <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Clinic Operations</h3>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'On Schedule Patients', value: '3', trend: '2 Checked-In', icon: Users, color: 'emerald' },
          { 
            label: 'Action Needed', 
            value: escalations.length > 0 ? escalations.length : '1', 
            trend: escalations.length > 0 ? 'Urgent Review Required' : '2 Missing Signatures', 
            icon: escalations.length > 0 ? AlertTriangle : FileSignature, 
            color: escalations.length > 0 ? 'red' : 'amber' 
          },
          { label: 'Waitlist Requests', value: '5', trend: '2 Slots Open', icon: Clock, color: 'blue' },
          { label: 'Payments Due', value: '₹4,200', trend: 'Across 8 patients (3 Collected)', icon: CreditCard, color: 'indigo' }
        ].map((stat, i) => (
          <div key={i} className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
            <div className={`w-10 h-10 rounded-lg bg-${stat.color}-100 text-${stat.color}-600 flex items-center justify-center mb-3`}><stat.icon size={20} /></div>
            <h3 className="text-slate-500 text-sm font-medium">{stat.label}</h3>
            <div className="flex items-end gap-3 mt-1">
              <span className="text-2xl font-bold text-slate-800">{stat.value}</span>
              <span className={`text-xs font-semibold mb-1 text-${stat.color}-600`}>{stat.trend}</span>
            </div>
          </div>
        ))}
      </div>

      <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">CuRA AI Metrics</h3>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Patient Sessions Handled', value: '30', trend: '+15% This Week', icon: MessageSquare, color: 'blue' },
          { label: 'AI Resolution Rate', value: '92%', trend: 'Highly Efficient', icon: CheckCircle2, color: 'emerald' },
          { label: 'Human Handoffs', value: '2', trend: 'Required Attention', icon: UserPlus, color: 'amber' },
          { label: 'Avg. Response Time', value: '1.2s', trend: 'Instantaneous', icon: Activity, color: 'purple' }
        ].map((stat, i) => (
          <div key={i} className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
            <div className={`w-10 h-10 rounded-lg bg-${stat.color}-100 text-${stat.color}-600 flex items-center justify-center mb-3`}><stat.icon size={20} /></div>
            <h3 className="text-slate-500 text-sm font-medium">{stat.label}</h3>
            <div className="flex items-end gap-3 mt-1">
              <span className="text-2xl font-bold text-slate-800">{stat.value}</span>
              <span className={`text-xs font-semibold mb-1 text-${stat.color}-600`}>{stat.trend}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col">
          <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50 rounded-t-xl">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2"><AlertTriangle size={18} className="text-amber-500" /> Clinical Triage Alerts (NLP Flagged)</h3>
            <span className="bg-red-100 text-red-700 text-xs px-2.5 py-1 rounded-full font-bold">{escalations.length} Active</span>
          </div>
          <div className="p-5 overflow-y-auto max-h-96">
            {escalations.length === 0 ? (
              <div className="text-center py-10 text-slate-400"><CheckCircle2 size={40} className="mx-auto text-emerald-200 mb-3" /><p>All patients stable. No critical NLP red flags detected.</p></div>
            ) : (
              <div className="space-y-4">
                {escalations.map((esc) => (
                  <div key={esc.id} className="bg-red-50/50 border border-red-100 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2"><span className="font-semibold text-slate-800">{esc.patient}</span><span className="text-xs text-slate-500">{esc.time}</span></div>
                    <p className="text-sm text-red-800 bg-white p-3 rounded border border-red-100 mb-4 font-medium">"{esc.message}"</p>
                    <div className="flex gap-2">
                      <button onClick={() => setActiveTab('inbox')} className="text-xs bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition font-bold shadow-sm">Take Over Chat</button>
                      <button onClick={() => { setEscalations(escalations.filter(e => e.id !== esc.id)); triggerToast('Alert resolved'); }} className="text-xs bg-white border border-slate-300 text-slate-600 px-4 py-2 rounded-lg hover:bg-slate-50 transition font-medium">Mark Resolved</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSchedule = () => {
    const days = Array.from({length: 30}, (_, i) => i + 1);
    const today = new Date().getDate();
    const currentDayOfWeek = new Date().getDay();
    
    const startOfWeek = today - currentDayOfWeek;
    const weekDays = Array.from({length: 7}, (_, i) => {
      let d = startOfWeek + i;
      if (d <= 0) d += 30;
      if (d > 30) d -= 30;
      return d;
    });

    const hours = ['08:00 AM', '09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM', '05:00 PM', '06:00 PM'];

    return (
      <div className="p-6 md:p-8 animate-in fade-in max-w-7xl mx-auto h-full flex flex-col pb-16">
         <div className="mb-6 flex justify-between items-end shrink-0">
           <div>
             <h2 className="text-2xl font-bold text-slate-800">Clinical Schedule</h2>
             <p className="text-slate-500 text-sm mt-1">Manage AI bookings, doctor availability, and appointments.</p>
           </div>
           <div className="flex gap-2 hidden sm:flex">
             <button onClick={() => setCalendarView('day')} className={`px-4 py-2 rounded-lg text-sm shadow-sm transition ${calendarView === 'day' ? 'bg-emerald-600 text-white font-bold' : 'bg-white border border-slate-200 text-slate-600 font-medium hover:bg-slate-50'}`}>Day</button>
             <button onClick={() => setCalendarView('week')} className={`px-4 py-2 rounded-lg text-sm shadow-sm transition ${calendarView === 'week' ? 'bg-emerald-600 text-white font-bold' : 'bg-white border border-slate-200 text-slate-600 font-medium hover:bg-slate-50'}`}>Week</button>
             <button onClick={() => setCalendarView('month')} className={`px-4 py-2 rounded-lg text-sm shadow-sm transition ${calendarView === 'month' ? 'bg-emerald-600 text-white font-bold' : 'bg-white border border-slate-200 text-slate-600 font-medium hover:bg-slate-50'}`}>Month</button>
           </div>
         </div>

         <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6 overflow-hidden">
            <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50 shrink-0">
                <h3 className="font-bold text-slate-800 text-lg flex items-center gap-2">
                  <button className="p-1 hover:bg-slate-200 rounded text-slate-400"><ChevronLeft size={18}/></button>
                  {calendarView === 'day' ? `Today, ${new Date().toLocaleString('default', { month: 'long', day: 'numeric', year: 'numeric' })}` : new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}
                  <button className="p-1 hover:bg-slate-200 rounded text-slate-400"><ChevronRight size={18}/></button>
                </h3>
              </div>
              <div className="flex-1 p-4 flex flex-col overflow-y-auto custom-scrollbar">
                
                {calendarView === 'month' && (
                  <>
                    <div className="grid grid-cols-7 gap-2 mb-2 shrink-0">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => <div key={d} className="text-center text-xs font-bold text-slate-400 uppercase tracking-wider">{d}</div>)}
                    </div>
                    <div className="grid grid-cols-7 gap-2 flex-1 auto-rows-fr min-h-[400px]">
                      <div className="border border-slate-100 rounded-lg p-2 bg-slate-50/50 text-slate-300">29</div>
                      <div className="border border-slate-100 rounded-lg p-2 bg-slate-50/50 text-slate-300">30</div>
                      <div className="border border-slate-100 rounded-lg p-2 bg-slate-50/50 text-slate-300">31</div>
                      {days.map(d => {
                        const isToday = d === today;
                        const hasAppts = isToday && appointments.length > 0;
                        return (
                          <div key={d} className={`border rounded-lg p-2 relative flex flex-col items-end ${isToday ? 'border-emerald-400 bg-emerald-50/30 ring-2 ring-emerald-400/20' : 'border-slate-100 hover:border-emerald-300 transition cursor-pointer'}`}>
                            <span className={`text-sm font-semibold w-7 h-7 flex items-center justify-center rounded-full ${isToday ? 'bg-emerald-500 text-white' : 'text-slate-700'}`}>{d}</span>
                            {hasAppts && (
                              <div className="w-full mt-auto space-y-1">
                                 <div className="bg-emerald-100 text-emerald-700 text-[10px] font-bold px-1.5 py-0.5 rounded truncate w-full text-center">{appointments.length} Appts</div>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </>
                )}

                {calendarView === 'week' && (
                  <>
                    <div className="grid grid-cols-7 gap-2 mb-2 shrink-0">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => <div key={d} className="text-center text-xs font-bold text-slate-400 uppercase tracking-wider">{d}</div>)}
                    </div>
                    <div className="grid grid-cols-7 gap-2 flex-1 min-h-[400px]">
                      {weekDays.map((d, i) => {
                        const isToday = d === today;
                        const hasAppts = isToday && appointments.length > 0;
                        return (
                          <div key={i} className={`border rounded-lg p-2 relative flex flex-col ${isToday ? 'border-emerald-400 bg-emerald-50/10 ring-2 ring-emerald-400/20' : 'border-slate-100'}`}>
                            <div className="flex justify-between items-start mb-2">
                               <span className={`text-sm font-semibold w-7 h-7 flex items-center justify-center rounded-full ${isToday ? 'bg-emerald-500 text-white' : 'text-slate-700'}`}>{d}</span>
                            </div>
                            <div className="flex-1 flex flex-col gap-1 overflow-y-auto custom-scrollbar">
                               {hasAppts && appointments.map((apt, aIdx) => (
                                 <div key={aIdx} className="bg-emerald-100 border border-emerald-200 text-emerald-800 text-[10px] px-1.5 py-1 rounded truncate w-full leading-tight font-medium">
                                   <span className="font-bold">{apt.time.split(' ')[0]}</span> - {apt.patientName}
                                 </div>
                               ))}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </>
                )}

                {calendarView === 'day' && (
                  <div className="flex-1 flex flex-col gap-0 min-h-[400px]">
                    {hours.map(hour => {
                      const hrPrefix = hour.split(':')[0];
                      const isAmPm = hour.split(' ')[1];
                      const hrAppts = appointments.filter(a => a.time.startsWith(hrPrefix) && a.time.includes(isAmPm));
                      
                      return (
                        <div key={hour} className="flex border-b border-slate-100 min-h-[70px]">
                          <div className="w-20 shrink-0 text-right pr-4 text-[11px] font-bold text-slate-400 mt-2">{hour}</div>
                          <div className="flex-1 border-l border-slate-200 pl-4 py-2 relative flex flex-col gap-2">
                             {hrAppts.map(apt => (
                               <div key={apt.id} className="bg-emerald-50 border border-emerald-200 p-2.5 rounded-lg flex items-center gap-3 shadow-sm max-w-sm hover:bg-emerald-100 transition cursor-pointer">
                                  <div className="w-8 h-8 rounded-full bg-emerald-200 text-emerald-700 flex justify-center items-center font-bold text-xs shrink-0">{apt.img}</div>
                                  <div className="flex-1 min-w-0">
                                     <div className="font-bold text-slate-800 text-sm truncate">{apt.patientName}</div>
                                     <div className="text-[10px] text-emerald-600 font-semibold uppercase">{apt.type} • {apt.time}</div>
                                  </div>
                               </div>
                             ))}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}

              </div>
            </div>

            <div className="flex flex-col gap-6 overflow-hidden h-full">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col shrink-0">
                <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50 rounded-t-xl">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                    <ToggleRight size={18} className="text-emerald-500" /> Availability (Today)
                  </h3>
                  <button
                    onClick={() => {
                      setIsDayActive(!isDayActive);
                      triggerToast(isDayActive ? 'Marked as Out of Office' : 'Marked as Available');
                    }}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${isDayActive ? 'bg-emerald-500' : 'bg-slate-300'}`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${isDayActive ? 'translate-x-4.5' : 'translate-x-1'}`} />
                  </button>
                </div>
                <div className="p-4 space-y-3">
                  {isDayActive ? timeSlots.map((slot, idx) => (
                    <div key={idx} className="flex justify-between items-center p-2 rounded-lg hover:bg-slate-50 transition">
                      <span className={`text-sm font-medium ${slot.active ? 'text-slate-800' : 'text-slate-400 line-through'}`}>{slot.time}</span>
                      <button
                        onClick={() => {
                          const newSlots = [...timeSlots];
                          newSlots[idx].active = !newSlots[idx].active;
                          setTimeSlots(newSlots);
                          triggerToast(`Slot ${slot.time} ${newSlots[idx].active ? 'Opened' : 'Closed'}`);
                        }}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${slot.active ? 'bg-blue-500' : 'bg-slate-300'}`}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${slot.active ? 'translate-x-4.5' : 'translate-x-1'}`} />
                      </button>
                    </div>
                  )) : (
                    <div className="text-center py-6 text-sm text-slate-500 bg-slate-50 rounded-lg border border-slate-100">
                      You are marked as unavailable for the entire day. AI will not offer any slots.
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col flex-1 overflow-hidden">
                <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50 shrink-0">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Clock size={18} className="text-emerald-500" /> Upcoming</h3>
                  <span className="bg-emerald-100 text-emerald-700 text-xs px-2.5 py-1 rounded-full font-bold">Today</span>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                  {appointments.map((apt) => (
                    <div key={apt.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:border-emerald-200 hover:bg-emerald-50/50 transition cursor-default">
                      <div className="w-14 flex flex-col items-center justify-center shrink-0 border-r border-slate-100 pr-3">
                        <span className="text-sm font-bold text-slate-800">{apt.time.split(' ')[0]}</span>
                        <span className="text-[9px] font-bold text-slate-500 uppercase">{apt.time.split(' ')[1]}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-slate-800 text-sm truncate">{apt.patientName}</h4>
                        <p className="text-[10px] text-slate-500 font-semibold truncate mt-0.5">{apt.type}</p>
                      </div>
                      <div className="shrink-0">
                        <span className={`text-[9px] font-bold px-2 py-1 rounded-full uppercase tracking-wider ${apt.status === 'Checked In' ? 'bg-emerald-100 text-emerald-700' : apt.status === 'Waiting' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                          {apt.status}
                        </span>
                      </div>
                    </div>
                  ))}
                  {appointments.length === 0 && (
                     <div className="text-center py-10 text-slate-400">
                       <p className="text-sm">No upcoming appointments.</p>
                     </div>
                  )}
                </div>
              </div>

            </div>
         </div>
      </div>
    );
  };

  const renderPatients = () => {
    const filteredPatients = patientsData.filter(p => p.name.toLowerCase().includes(patientSearch.toLowerCase()) || p.caseNo.toLowerCase().includes(patientSearch.toLowerCase()));

    return (
      <div className="p-6 md:p-8 animate-in fade-in max-w-7xl mx-auto h-full flex flex-col pb-16">
        <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 shrink-0">
           <div>
             <h2 className="text-2xl font-bold text-slate-800">Patient Directory</h2>
             <p className="text-slate-500 text-sm mt-1">Manage active IVF cycles and access detailed medical records.</p>
           </div>
           <button onClick={() => setIsAddPatientModalOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-lg text-sm font-bold shadow-sm transition flex items-center gap-2 shrink-0">
              <UserPlus size={16} /> Add New Patient
           </button>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col flex-1 overflow-hidden">
           <div className="p-4 border-b border-slate-100 bg-slate-50 flex items-center shrink-0">
              <div className="relative w-full max-w-md">
                 <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                 <input 
                   type="text" 
                   placeholder="Search by patient name or Case ID..." 
                   value={patientSearch}
                   onChange={(e) => setPatientSearch(e.target.value)}
                   className="w-full pl-10 pr-4 py-2 text-sm bg-white border border-slate-200 rounded-lg outline-none focus:border-emerald-500 transition"
                 />
              </div>
           </div>
           <div className="flex-1 overflow-y-auto custom-scrollbar p-0">
              <table className="w-full text-left border-collapse min-w-[800px]">
                 <thead className="bg-slate-50/50 text-[10px] font-bold uppercase text-slate-500 tracking-wider sticky top-0 backdrop-blur-sm z-10 border-b border-slate-200">
                    <tr>
                       <th className="p-4">Patient Info</th>
                       <th className="p-4">Case ID</th>
                       <th className="p-4">Current Stage</th>
                       <th className="p-4">Status</th>
                       <th className="p-4 text-right">Actions</th>
                    </tr>
                 </thead>
                 <tbody className="divide-y divide-slate-100">
                    {filteredPatients.map(pt => (
                       <tr key={pt.id} className="hover:bg-slate-50 transition cursor-pointer group" onClick={() => { setSelectedPatientId(pt.id); setActiveTab('patient_detail'); }}>
                          <td className="p-4 flex items-center gap-3">
                             <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex justify-center items-center font-bold text-xs shrink-0">{pt.img}</div>
                             <div>
                                <div className="font-bold text-slate-800 text-sm">{pt.name}</div>
                                <div className="text-[10px] font-semibold text-slate-500">Age: {pt.age}</div>
                             </div>
                          </td>
                          <td className="p-4">
                             <span className="bg-slate-100 text-slate-600 text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider border border-slate-200">{pt.caseNo}</span>
                          </td>
                          <td className="p-4">
                             <span className="text-sm font-semibold text-slate-700">{pt.stage}</span>
                          </td>
                          <td className="p-4">
                             <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${pt.status === 'Active' ? 'bg-emerald-100 text-emerald-700' : pt.status === 'Completed' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}`}>
                                {pt.status}
                             </span>
                          </td>
                          <td className="p-4 text-right">
                             <button className="text-emerald-600 font-semibold text-xs px-4 py-2 rounded-lg border border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 transition-all shadow-sm">View Chart</button>
                          </td>
                       </tr>
                    ))}
                    {filteredPatients.length === 0 && (
                      <tr>
                        <td colSpan="5" className="p-8 text-center text-slate-400 text-sm font-medium">No patients found matching your search.</td>
                      </tr>
                    )}
                 </tbody>
              </table>
           </div>
        </div>
      </div>
    );
  };

  const renderPatientDetail = () => {
    const pt = patientsData.find(p => p.id === selectedPatientId);
    if (!pt) return <div className="p-8">Patient not found</div>;

    return (
      <div className="p-6 md:p-8 animate-in fade-in max-w-7xl mx-auto h-full flex flex-col pb-16">
         <button onClick={() => setActiveTab('patients')} className="flex items-center gap-2 text-slate-500 hover:text-emerald-600 transition font-semibold text-sm mb-6 w-fit">
            <ArrowLeft size={16} /> Back to Directory
         </button>
         
         <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4 shrink-0">
            <div className="flex items-center gap-4">
               <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-700 flex justify-center items-center font-bold text-xl shrink-0">{pt.img}</div>
               <div>
                  <h2 className="text-2xl font-bold text-slate-800">{pt.name}</h2>
                  <div className="flex items-center gap-3 mt-1">
                     <span className="bg-slate-100 text-slate-600 text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider border border-slate-200">{pt.caseNo}</span>
                     <span className="text-xs font-semibold text-slate-500">Age: {pt.age}</span>
                     <span className="text-xs font-semibold text-slate-500 flex items-center gap-1"><Phone size={12}/> {pt.phone}</span>
                  </div>
               </div>
            </div>
            <div className="flex flex-col items-start md:items-end gap-2">
               <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${pt.status === 'Active' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>{pt.status} • {pt.stage}</span>
               <div className="flex gap-2">
                  <button onClick={() => setActiveTab('inbox')} className="bg-emerald-50 text-emerald-700 px-4 py-2 rounded-lg text-sm font-semibold border border-emerald-200 hover:bg-emerald-100 transition flex items-center gap-2"><MessageSquare size={14}/> Message</button>
               </div>
            </div>
         </div>

         <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1 overflow-hidden">
            <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col overflow-hidden">
               <div className="p-4 border-b border-slate-100 bg-slate-50 shrink-0">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Activity size={18} className="text-emerald-500" /> Clinical Journey</h3>
               </div>
               <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
                  {pt.timeline.map((event, idx) => (
                     <div key={idx} className="flex gap-4">
                        <div className="flex flex-col items-center">
                           <div className="w-3 h-3 rounded-full bg-emerald-500 mt-1.5 shrink-0"></div>
                           {idx !== pt.timeline.length - 1 && <div className="w-0.5 bg-emerald-100 flex-1 mt-2"></div>}
                        </div>
                        <div className="flex-1 bg-slate-50 border border-slate-100 p-4 rounded-xl">
                           <div className="flex justify-between items-start mb-1">
                              <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">{event.type}</span>
                              <span className="text-xs font-semibold text-slate-400">{event.date}</span>
                           </div>
                           <p className="text-sm font-bold text-slate-800">{event.title}</p>
                        </div>
                     </div>
                  ))}
                  {pt.timeline.length === 0 && (
                     <p className="text-center text-sm text-slate-500 py-10">No clinical events logged yet.</p>
                  )}
               </div>
            </div>

            <div className="flex flex-col gap-6 overflow-hidden">
               <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 shrink-0">
                  <h3 className="font-semibold text-slate-800 mb-4 text-sm">Quick Actions</h3>
                  <div className="space-y-2">
                     <button onClick={() => { 
                       addTimelineEvent(pt.id, { type: 'Lab', title: 'Standard Lab Order Generated' }); 
                       addMessage('bot', 'Please find your standard Lab Order attached. Kindly get these tests done before your next visit.', null, { isPdf: true, filename: 'Lab_Order_GEN.pdf' });
                       triggerToast("Added standard Lab Order"); 
                     }} className="w-full bg-slate-50 border border-slate-200 text-slate-700 px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-slate-100 transition flex items-center gap-2"><FileText size={16} className="text-blue-500"/> Generate Lab Order</button>
                     <button onClick={() => { 
                       addTimelineEvent(pt.id, { type: 'Admin', title: 'IVF Consent Form Sent via WhatsApp' }); 
                       addMessage('bot', 'Please review and digitally sign the IVF Consent Form attached below prior to your procedure.', null, { isPdf: true, filename: 'IVF_Consent_Form.pdf' });
                       triggerToast("Consent form sent via WhatsApp"); 
                     }} className="w-full bg-slate-50 border border-slate-200 text-slate-700 px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-slate-100 transition flex items-center gap-2"><FileText size={16} className="text-amber-500"/> Send IVF Consent</button>
                  </div>
               </div>
               
               <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col flex-1 overflow-hidden">
                  <div className="p-4 border-b border-slate-100 bg-slate-50 shrink-0">
                     <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-2"><Edit3 size={16} className="text-emerald-500"/> Medical Notes</h3>
                  </div>
                  <div className="flex-1 p-4 bg-slate-50/50">
                     <textarea value={clinicalNote} onChange={(e) => setClinicalNote(e.target.value)} placeholder="Type clinical notes here..." className="w-full h-full bg-white border border-slate-200 rounded-lg p-3 text-sm resize-none outline-none focus:border-emerald-500"></textarea>
                  </div>
                  <div className="p-3 border-t border-slate-100 bg-white shrink-0">
                     <button onClick={() => { if(!clinicalNote.trim()) return; addTimelineEvent(pt.id, { type: 'Note', title: clinicalNote }); setClinicalNote(''); triggerToast("Notes Saved"); }} className="w-full bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-sm hover:bg-emerald-700 transition">Save Notes</button>
                  </div>
               </div>
            </div>
         </div>
      </div>
    );
  };

  const renderSharedInbox = () => (
    <div className="flex h-full animate-in fade-in">
      <div className="w-80 border-r border-slate-200 bg-white flex flex-col hidden lg:flex">
        <div className="p-4 border-b border-slate-200"><div className="bg-slate-100 rounded-lg flex items-center px-3 py-2"><Search size={16} className="text-slate-400" /><input type="text" placeholder="Search patients..." className="bg-transparent border-none focus:ring-0 text-sm ml-2 w-full outline-none" /></div></div>
        <div className="flex-1 overflow-y-auto">
          <div className={`p-4 border-b border-slate-100 cursor-pointer flex gap-3 relative ${escalations.length > 0 ? 'bg-red-50/30' : 'bg-blue-50/50'}`}>
            <div className={`absolute left-0 top-0 bottom-0 w-1 ${escalations.length > 0 ? 'bg-red-500' : 'bg-blue-500'}`}></div>
            <div className={`w-10 h-10 rounded-full flex justify-center items-center font-bold ${escalations.length > 0 ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>PT</div>
            <div className="flex-1 min-w-0">
              <div className="flex justify-between items-center mb-1"><h4 className="font-semibold text-slate-800 text-sm">Current Patient</h4><span className="text-xs text-slate-500">Now</span></div>
              <p className={`text-xs truncate ${escalations.length > 0 ? 'text-red-600 font-semibold' : 'text-slate-600'}`}>{messages[messages.length-1]?.text || 'No messages'}</p>
            </div>
          </div>
          {['Priya Sharma', 'Anjali Desai', 'Kavita Rao'].map((name, i) => (<div key={i} className="p-4 border-b border-slate-100 cursor-pointer flex gap-3 hover:bg-slate-50 transition opacity-60"><div className="w-10 h-10 rounded-full bg-slate-100 text-slate-600 flex justify-center items-center font-bold">{name.split(' ').map(n=>n[0]).join('')}</div><div className="flex-1 min-w-0"><div className="flex justify-between items-center mb-1"><h4 className="font-semibold text-slate-800 text-sm">{name}</h4><span className="text-xs text-slate-400">Yesterday</span></div><p className="text-xs text-slate-500 truncate">CuRA: Please review your scan results.</p></div></div>))}
        </div>
      </div>
      <div className="flex-1 flex flex-col bg-slate-50">
        <div className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 shadow-sm z-10">
          <div className="flex items-center gap-3"><div className="w-9 h-9 rounded-full bg-blue-100 text-blue-600 flex justify-center items-center font-bold">PT</div><div><h3 className="font-bold text-slate-800">Current Patient</h3>
            {isHumanControl ? (
              <p className="text-xs text-amber-600 font-medium flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span> Human First</p>
            ) : (
              <p className="text-xs text-emerald-600 font-medium flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> AI & Human Co-Pilot</p>
            )}
          </div></div>
          <div className="flex gap-2">
            {!isHumanControl && (
              <button onClick={() => { setIsHumanControl(true); triggerToast('Human agent took control. AI paused.'); }} className="text-sm bg-amber-500 text-white px-4 py-2 rounded-lg hover:bg-amber-600 font-medium shadow-sm transition flex items-center gap-2"><UserPlus size={14}/> Take Control</button>
            )}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
           <div className="text-center"><span className="bg-slate-200 text-slate-600 text-[10px] uppercase tracking-wider font-bold px-3 py-1 rounded-full">Today</span></div>
           {messages.map((msg) => {
             if (msg.sender === 'system') return null;
             const isPatient = msg.sender === 'user';
             const isHumanAgent = msg.sender === 'admin';
             return (
               <div key={msg.id} className={`flex ${isPatient ? 'justify-start' : 'justify-end'}`}>
                 <div className={`max-w-[70%] rounded-2xl p-4 shadow-sm ${isPatient ? 'bg-white border border-slate-200 rounded-tl-sm' : isHumanAgent ? 'bg-blue-600 text-white rounded-tr-sm' : 'bg-emerald-50 border border-emerald-100 rounded-tr-sm'}`}>
                   <div className="flex items-center gap-2 mb-1.5 opacity-80">{isPatient ? <User size={12}/> : isHumanAgent ? <Users size={12}/> : <Activity size={12}/>}<span className="text-[11px] font-bold uppercase">{isPatient ? 'Patient' : isHumanAgent ? 'Dr. Narmada (Human)' : 'CuRA AI'}</span></div>
                   {msg.metadata?.isPdf && (<div className="bg-white/80 rounded p-2 mb-2 flex items-center gap-3 border border-black/10"><FileText size={20} className={isHumanAgent ? 'text-blue-200' : 'text-red-500'} /><span className="text-sm font-semibold truncate">{msg.metadata.filename}</span></div>)}
                   <p className={`text-sm whitespace-pre-wrap ${isHumanAgent ? 'text-white' : 'text-slate-800'}`}>{msg.text}</p>
                   <div className={`text-[10px] text-right mt-2 ${isHumanAgent ? 'text-blue-200' : 'text-slate-400'}`}>{msg.timestamp}</div>
                 </div>
               </div>
             )
           })}
           <div ref={adminChatEndRef} />
        </div>
        <div className="p-4 bg-white border-t border-slate-200">
          <div className="bg-slate-100 rounded-xl flex items-end px-4 py-2 border border-slate-200 focus-within:border-blue-400 transition-colors">
            <textarea value={adminInputText} onChange={(e) => setAdminInputText(e.target.value)} placeholder="Type message to patient... (This will pause CuRA AI and take over)" className="flex-1 bg-transparent border-none outline-none resize-none py-2 text-sm text-slate-800 max-h-32" rows={1} onKeyDown={(e) => { if(e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAdminSend(); } }} />
            <div className="flex items-center gap-2 pb-2"><button onClick={handleAdminSend} className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 shadow-sm"><Send size={16}/></button></div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDevicePreview = () => (
    <div className={`transition-all duration-300 ease-in-out border-l border-slate-200 bg-[#fdfcfb] relative flex flex-col justify-center items-center overflow-y-auto shadow-[-10px_0_15px_-3px_rgba(0,0,0,0.05)] ${isDeviceOpen || userRole === 'patient' ? 'w-[400px] min-w-[360px] opacity-100' : 'w-0 min-w-0 opacity-0 overflow-hidden'} ${userRole === 'patient' ? '!w-full flex-1' : ''}`}>
      {userRole !== 'patient' && (
        <div className="absolute top-4 w-full flex justify-between px-6 items-center"><span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1"><Activity size={12}/> Live Patient Preview</span><button onClick={() => setIsDeviceOpen(false)} className="bg-slate-100 p-1.5 rounded-full hover:bg-slate-200 text-slate-500 transition"><X size={16}/></button></div>
      )}
      <div className="relative w-[320px] h-[660px] flex-shrink-0 bg-black rounded-[3rem] p-1.5 shadow-2xl border-[2px] border-gray-800 my-auto">
        <div className="absolute top-28 -left-1 w-1 h-10 bg-gray-800 rounded-l-md"></div><div className="absolute top-40 -left-1 w-1 h-10 bg-gray-800 rounded-l-md"></div><div className="absolute top-32 -right-1 w-1 h-14 bg-gray-800 rounded-r-md"></div>
        <div className="w-full h-full bg-[#EFE6DD] rounded-[2.6rem] overflow-hidden relative flex flex-col">
          <div className="absolute top-2 inset-x-0 mx-auto w-[90px] h-[26px] bg-black rounded-full z-40 flex items-center justify-between px-2 shadow-sm"><div className="w-2 h-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-full opacity-80"></div><div className="w-2 h-2 bg-emerald-500 rounded-full opacity-30 blur-[2px]"></div></div>
          <div className="h-10 w-full pt-3 px-6 flex justify-between items-center text-[12px] font-bold z-30 bg-transparent absolute top-0 text-black"><span className="tracking-tight mt-0.5">18:18</span><div className="flex gap-1.5 items-center mr-0 mt-0.5"><Signal size={14} className="fill-current" strokeWidth={2.5} /><Wifi size={14} className="fill-current" strokeWidth={2.5} /><Battery size={16} className="fill-current opacity-90" /></div></div>
          <div className="bg-[#f6f6f6]/95 backdrop-blur-md text-black px-2 pt-11 pb-2 flex items-center justify-between z-20 border-b border-gray-300">
            <div className="flex items-center gap-1">
              <button className="text-[#007AFF] flex items-center"><ChevronLeft size={28} strokeWidth={2.5} /></button>
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden border border-gray-300 ml-0.5"><User size={20} className="text-gray-400" fill="currentColor" /></div>
              <div className="ml-2 flex flex-col justify-center"><h2 className="font-semibold text-[15px] flex items-center gap-1 leading-none mb-1">Genesis Fertility <div className="bg-[#00A884] rounded-full w-[12px] h-[12px] flex items-center justify-center"><CheckCheck size={8} className="text-white" strokeWidth={3} /></div></h2><p className="text-[11px] text-gray-500 leading-none">tap here for contact info</p></div>
            </div>
            <div className="flex items-center gap-3 text-[#007AFF] pr-3"><Video size={24} strokeWidth={1.5} /><Phone size={22} strokeWidth={1.5} /></div>
          </div>
          <div className="flex-1 bg-[#EFE6DD] p-3 overflow-y-auto relative custom-scrollbar flex flex-col gap-3" style={{backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10 10h10v10H10zM30 30h10v10H30z' fill='%23000000' fill-opacity='0.035' fill-rule='evenodd'/%3E%3C/svg%3E")`, backgroundSize: '120px'}}>
            <div className="flex justify-center mt-2 mb-1"><span className="bg-[#f0f2f5]/80 backdrop-blur-sm text-[#54656f] text-[11px] font-medium px-3 py-1 rounded-lg shadow-sm">Today</span></div>
            <div className="flex justify-center mb-3"><div className="bg-[#FCE19B] text-[#8C6D1F] text-[11px] px-4 py-2.5 rounded-lg shadow-sm text-center max-w-[95%] leading-snug">🔒 Messages are end-to-end encrypted. <span className="font-bold cursor-pointer hover:underline">Learn more</span></div></div>
            {messages.map((msg) => {
              if (msg.sender === 'system') return null;
              const isUser = msg.sender === 'user';
              return (
                <div key={msg.id} className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} animate-in fade-in slide-in-from-bottom-2 duration-300`}>
                  <div className={`relative max-w-[85%] rounded-2xl px-3 py-1.5 text-[15px] shadow-sm leading-snug ${isUser ? 'bg-[#E1F7CB] rounded-br-sm' : 'bg-white rounded-tl-sm'}`}>
                    {msg.metadata?.isPdf && (<div className="bg-black/5 rounded-xl p-2 mb-2 flex items-center gap-3 border border-black/5"><div className="bg-red-500 text-white p-2.5 rounded-lg"><FileText size={22} /></div><div className="flex-1 min-w-0 pr-2"><p className="text-[14px] font-semibold text-gray-800 truncate leading-tight">{msg.metadata.filename}</p><p className="text-[12px] text-gray-500 mt-0.5">1.2 MB • PDF</p></div></div>)}
                    <span className="text-[#111b21] whitespace-pre-wrap">{msg.text}</span>
                    <div className={`flex justify-end items-center gap-1 mt-1 -mb-0.5 ${msg.metadata?.isPdf ? 'pt-1' : ''}`}><span className="text-[11px] text-gray-500 opacity-90">{msg.timestamp}</span>{isUser && <CheckCheck size={16} className="text-[#53bdeb]" />}</div>
                  </div>
                  {msg.options && (
                    <div className="flex flex-col gap-1.5 mt-1.5 w-full max-w-[85%]">
                      {msg.options.map((opt, i) => <button key={i} onClick={opt.action} className="bg-white border border-gray-200 text-[#007AFF] shadow-sm text-[14px] font-medium py-2 px-3 rounded-xl hover:bg-gray-50 transition active:scale-95 text-center w-full">{opt.label}</button>)}
                    </div>
                  )}
                </div>
              );
            })}
            {isTyping && (<div className="flex items-start"><div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm flex items-center gap-1.5"><div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div><div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100"></div><div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200"></div></div></div>)}
            <div ref={chatEndRef} />
          </div>
          <div className="bg-[#f6f6f6] px-2 pt-2 pb-6 flex items-end gap-2 relative z-10 border-t border-gray-300">
            <button className="text-[#007AFF] p-1.5 flex-shrink-0 mb-1 cursor-pointer"><Plus size={28} /></button>
            <div className="flex-1 bg-white rounded-full flex items-end px-3 py-1 border border-gray-300 shadow-sm min-h-[38px] max-h-[100px] mb-1">
              <textarea value={inputText} onChange={(e) => setInputText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleUserSend(); } }} placeholder="Message" className="flex-1 bg-transparent outline-none resize-none py-[6px] text-[16px] leading-tight custom-scrollbar" rows={1} style={{minHeight: '24px'}} />
            </div>
            {inputText.trim() ? (<button onClick={() => handleUserSend()} className="bg-[#007AFF] text-white p-2 rounded-full shadow-sm hover:bg-blue-600 transition flex-shrink-0 mb-1"><Send size={18} className="ml-0.5" /></button>) : (<button className="text-[#007AFF] p-2 flex-shrink-0 mb-0.5 cursor-pointer"><Mic size={26} /></button>)}
          </div>
        </div>
      </div>
    </div>
  );

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <button onClick={onExit} className="mb-6 text-slate-500 hover:text-slate-800 transition flex items-center gap-2 font-medium">
          <ArrowLeft size={16} /> Back to Portal Hub
        </button>
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-slate-100">
          <div className="text-center mb-8">
            <div className="bg-teal-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"><Activity className="text-teal-600 w-8 h-8" /></div>
            <h1 className="text-2xl font-bold text-slate-800">CuRA AI Platform</h1>
            <p className="text-slate-500 mt-2 text-sm">Doctor / Admin Secure Portal</p>
          </div>
          <div className="space-y-4">
            <div className="bg-slate-50 text-slate-600 p-4 rounded-xl text-xs border border-slate-200 text-center">
              <p className="font-bold text-slate-800">Demo Login</p>
              <p>Username: <b>doctor</b> | Password: <b>doctor123</b></p>
            </div>
            {loginError && <p className="text-red-500 text-sm font-medium">{loginError}</p>}
            <form onSubmit={handleLogin} className="space-y-4 pt-2">
              <input type="text" placeholder="Username" className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none text-sm" value={username} onChange={e => setUsername(e.target.value)} required />
              <input type="password" placeholder="Password" className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none text-sm" value={password} onChange={e => setPassword(e.target.value)} required />
              <button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3 rounded-xl transition shadow-md">Authenticate</button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (userRole === 'patient') return <div className="min-h-screen bg-black flex flex-col justify-center items-center relative"><button onClick={handleLogout} className="absolute top-4 left-4 text-white/50 hover:text-white z-50 text-xs font-bold p-2 bg-white/10 rounded-lg">Logout Session</button>{renderDevicePreview()}</div>;

  return (
    <div className="flex h-screen bg-white font-sans overflow-hidden text-slate-800">
      {renderSidebar()}
      <div className="flex-1 flex flex-col relative overflow-hidden">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-8 z-10 flex-shrink-0 shadow-sm">
           <div className="flex items-center gap-3 md:hidden"><Menu className="text-slate-500" /><h1 className="font-bold text-lg text-slate-800">CuRA AI</h1></div>
           <div className="hidden md:block"><h2 className="font-bold text-slate-800 capitalize tracking-wide">{activeTab.replace('_', ' ')}</h2></div>
           <div className="flex items-center gap-4">{!isDeviceOpen && (<button onClick={() => setIsDeviceOpen(true)} className="flex items-center gap-2 text-sm font-semibold bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-lg border border-emerald-200 hover:bg-emerald-100 transition">📱 Show Device Preview</button>)}</div>
        </header>
        <div className="flex-1 overflow-y-auto bg-slate-50/50 relative custom-scrollbar">
          {activeTab === 'testbench' && renderTestBench()}
          {activeTab === 'knowledge' && renderKnowledgeBase()}
          {activeTab === 'dashboard' && renderDashboard()}
          {activeTab === 'schedule' && renderSchedule()}
          {activeTab === 'patients' && renderPatients()}
          {activeTab === 'patient_detail' && renderPatientDetail()}
          {activeTab === 'inbox' && renderSharedInbox()}
        </div>
      </div>
      {renderDevicePreview()}
      {toastMsg && (<div className="fixed bottom-6 right-1/2 transform translate-x-1/2 md:translate-x-0 md:right-auto md:left-6 bg-slate-800 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-3 z-[110]"><CheckCircle2 size={18} className="text-emerald-400" /><span className="text-sm font-medium">{toastMsg}</span></div>)}
      
      {isAddPatientModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-[120] p-4 animate-in fade-in">
           <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95">
              <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                 <h3 className="font-bold text-slate-800 text-lg">Add New Patient</h3>
                 <button onClick={() => setIsAddPatientModalOpen(false)} className="text-slate-400 hover:bg-slate-200 p-1.5 rounded-full transition"><X size={18}/></button>
              </div>
              <form className="p-6 space-y-4">
                 <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Full Name</label>
                    <input type="text" required value={newPatientForm.name} onChange={e=>setNewPatientForm({...newPatientForm, name: e.target.value})} className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-emerald-500" placeholder="e.g. Priya Sharma" />
                 </div>
                 <div className="grid grid-cols-2 gap-4">
                    <div>
                       <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Age</label>
                       <input type="number" required min="18" max="65" value={newPatientForm.age} onChange={e=>setNewPatientForm({...newPatientForm, age: e.target.value})} className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-emerald-500" placeholder="e.g. 32" />
                    </div>
                    <div>
                       <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Phone</label>
                       <input type="text" required value={newPatientForm.phone} onChange={e=>setNewPatientForm({...newPatientForm, phone: e.target.value})} className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-emerald-500" placeholder="+91..." />
                    </div>
                 </div>
                 <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Initial Stage</label>
                    <select value={newPatientForm.stage} onChange={e=>setNewPatientForm({...newPatientForm, stage: e.target.value})} className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-emerald-500 bg-white">
                       <option>Initial Consultation</option>
                       <option>Stimulation</option>
                       <option>Egg Retrieval (OPU)</option>
                       <option>Embryo Transfer</option>
                       <option>Two-Week Wait</option>
                    </select>
                 </div>
                 <div className="pt-4 flex gap-3">
                    <button type="button" onClick={() => setIsAddPatientModalOpen(false)} className="flex-1 bg-white border border-slate-200 text-slate-600 px-4 py-2.5 rounded-lg text-sm font-bold hover:bg-slate-50 transition">Cancel</button>
                    <button type="button" onClick={handleAddPatientSubmit} className="flex-1 bg-emerald-600 text-white px-4 py-2.5 rounded-lg text-sm font-bold hover:bg-emerald-700 transition shadow-sm">Save Patient</button>
                 </div>
              </form>
           </div>
        </div>
      )}

    </div>
  );
};

// ============================================================================
// 3. MAIN PORTAL HUB (LANDING PAGE) & APP ENTRY
// ============================================================================

export default function App() {
  const [currentApp, setCurrentApp] = useState('landing');

  const LandingPage = () => (
    <div className="min-h-screen bg-slate-900 relative overflow-x-hidden flex items-center justify-center p-6 selection:bg-teal-500/30 font-sans">
      
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] rounded-full bg-teal-600/30 blur-[120px] mix-blend-screen"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[60vw] h-[60vw] rounded-full bg-blue-800/40 blur-[150px] mix-blend-screen"></div>
        <div className="absolute top-[40%] left-[60%] w-[30vw] h-[30vw] rounded-full bg-indigo-600/20 blur-[100px] mix-blend-screen"></div>
      </div>

      <div className="relative z-10 max-w-5xl w-full flex flex-col lg:flex-row items-center gap-16 py-12">
        
        <div className="flex-1 text-center lg:text-left space-y-8 animate-in fade-in slide-in-from-left-8 duration-700">
          
          <div className="flex items-center justify-center lg:justify-start gap-4 mb-4 filter drop-shadow-lg text-white">
             <div className="bg-gradient-to-tr from-teal-400 to-blue-600 p-4 rounded-2xl shadow-xl">
               <Heart size={48} className="text-white fill-current" />
             </div>
             <div className="flex flex-col">
               <span className="text-4xl font-black tracking-tight leading-none">Genesis</span>
               <span className="text-2xl font-light tracking-widest uppercase text-teal-300">Fertility Centre</span>
             </div>
          </div>

          <div className="space-y-4">
            <h1 className="text-5xl md:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white to-teal-100 leading-tight">
              Welcome to CuRA AI
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 font-light leading-relaxed max-w-2xl mx-auto lg:mx-0">
              The Intelligent WhatsApp Automation System configured specifically for Genesis Fertility Centre.
            </p>
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-slate-300 text-sm font-medium">
            <ShieldCheck size={16} className="text-teal-400" /> Medical-Grade AI Integration
          </div>
        </div>

        <div className="flex-1 w-full max-w-md mx-auto lg:mx-0 animate-in fade-in slide-in-from-right-8 duration-700">
          <div 
            onClick={() => setCurrentApp('demo')}
            className="bg-white/10 backdrop-blur-xl border border-white/20 shadow-[0_8px_32px_0_rgba(31,38,135,0.37)] rounded-3xl p-8 md:p-10 flex flex-col items-center text-center hover:bg-white/15 transition-all duration-300 group cursor-pointer hover:scale-[1.02]"
          >
            <div className="w-32 h-32 mb-6 bg-gradient-to-br from-blue-500/20 to-teal-500/20 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-500 border border-blue-500/30 relative overflow-hidden">
               <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-30"></div>
              <Activity size={48} className="text-teal-400 relative z-10" />
            </div>
            <h3 className="text-3xl font-bold text-white mb-4">Doctor's Workbench</h3>
            <p className="text-slate-300 text-base mb-8 leading-relaxed flex-1">
              Test clinical accuracy, edit custom RAG protocols, and experience the WhatsApp UI just as your patients would.
            </p>
            <button className="w-full py-4 bg-blue-600/90 hover:bg-blue-500 text-white rounded-xl font-bold text-lg transition-colors flex justify-center items-center gap-2 shadow-lg shadow-blue-900/50 border border-blue-400/50">
              Launch Demo <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background-color: rgba(156, 163, 175, 0.5); border-radius: 20px; }
      `}</style>
      {currentApp === 'landing' && <LandingPage />}
      {currentApp === 'demo' && <DemoWorkbench onExit={() => setCurrentApp('landing')} />}
    </>
  );
}